import { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { tagService } from '../services';
import { trackingEvent } from '../analytics/index';
import { AMPLITUDE_SERVICE } from '../analytics/constants';
import { getBasicAmplitudEventProperties } from '../analytics/utils';
import {
  fetchTagsRequested,
  fetchTagsSucceeded,
  fetchTagsFailed,
  getAllTagsSelector,
  getTagsLoadingSelector,
  getTagsErrorSelector,
  addTag,
  updateTag,
  removeTag,
  clearTags
} from '../redux/slices/tagSlice';

const modulePropNames = {
  schedule: 'to_use_react_gantt',
  lookahead: 'ganttVisualization'
};

/**
 * Custom hook for managing tags with Redux integration
 * Handles fetching, creating, updating, and deleting tags
 */
export const useTags = (module) => {
  if (!module || !modulePropNames[module]) {
    throw new Error(
      `Invalid module: ${module}. Supported modules: ${Object.keys(
        modulePropNames
      ).join(', ')}`
    );
  }

  const dispatch = useDispatch();

  // Redux state
  const tagsFromRedux = useSelector(getAllTagsSelector);
  const loadingFromRedux = useSelector(getTagsLoadingSelector);
  const errorFromRedux = useSelector(getTagsErrorSelector);
  const projectState = useSelector((state) => state.projectState);

  // Local state for additional functionality
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  /**
   * Handles gantt tag assignment logic
   */
  const handleGanttTagAssignment = useCallback((tags) => {
    const gantt = window[modulePropNames[module]];
    if (!gantt) return;

    gantt.toSelectTags = tags;
    const idToAssignLastTag = gantt.toHardAssignTag;

    if (!idToAssignLastTag || tags.length === 0) return;

    const objectTask = gantt.getTask(idToAssignLastTag);
    if (!objectTask?.tags) return;

    const lastTagObject = tags.reduce((prev, current) =>
      current.id > prev.id ? current : prev
    );

    objectTask.tags.push(lastTagObject);

    trackingEvent(
      'activity_tag_entry',
      {
        ...getBasicAmplitudEventProperties(),
        activity_name: objectTask?.text,
        activity_id: objectTask?.id,
        activity_index: objectTask?.correlative_id
      },
      AMPLITUDE_SERVICE
    );

    delete gantt.toHardAssignTag;
    gantt.refreshData();
  }, []);

  /**
   * Basic function that only fetches tags and dispatches to Redux
   */
  const fetchTags = useCallback(
    async (projectId) => {
      if (!projectId) {
        const error = 'No project ID provided';
        setLocalError(error);
        dispatch(fetchTagsFailed(error));
        return null;
      }

      dispatch(fetchTagsRequested());
      setLocalLoading(true);
      setLocalError(null);

      try {
        const response = await tagService.showByProject(projectId);

        if (!response?.tag) {
          const error = 'No tags found';
          setLocalError(error);
          dispatch(fetchTagsFailed(error));
          return [];
        }

        // Dispatch to Redux store
        dispatch(fetchTagsSucceeded(response.tag));

        return response.tag;
      } catch (err) {
        const errorMessage = err.message || 'Failed to fetch tags';
        setLocalError(errorMessage);
        dispatch(fetchTagsFailed(errorMessage));
        return null;
      } finally {
        setLocalLoading(false);
      }
    },
    [dispatch]
  );

  /**
   * Fetches tags by project and handles Gantt integration
   */
  const fetchTagsByProject = useCallback(
    async (projectId = null) => {
      const targetProjectId = projectId || projectState.projectSelected;

      if (!targetProjectId) {
        const error = 'No project selected';
        setLocalError(error);
        dispatch(fetchTagsFailed(error));
        return null;
      }

      // Use the basic fetch function
      const tags = await fetchTags(targetProjectId);

      if (tags) {
        // Handle gantt integration
        handleGanttTagAssignment(tags);

        // Set tags for gantt use
        if (window.to_use_react_gantt) {
          window.to_use_react_gantt.toSelectTags = tags;
        }
      }

      return tags;
    },
    [
      fetchTags,
      projectState.projectSelected,
      handleGanttTagAssignment,
      dispatch
    ]
  );

  /**
   * Creates a new tag
   */
  const createTag = useCallback(
    async (tagData) => {
      setLocalLoading(true);
      setLocalError(null);

      try {
        const response = await tagService.create(tagData);

        if (response) {
          // Add to Redux store
          dispatch(addTag(response));

          // Track the event
          trackingEvent(
            'tag_created',
            {
              ...getBasicAmplitudEventProperties(),
              tag_name: response.name,
              tag_id: response.id,
              project_id: tagData.projectId
            },
            AMPLITUDE_SERVICE
          );

          return response;
        }

        return null;
      } catch (err) {
        const errorMessage = err.message || 'Failed to create tag';
        setLocalError(errorMessage);
        return null;
      } finally {
        setLocalLoading(false);
      }
    },
    [dispatch]
  );

  /**
   * Updates an existing tag
   */
  const updateTagById = useCallback(
    async (tagId, tagData) => {
      setLocalLoading(true);
      setLocalError(null);

      try {
        const response = await tagService.update({ id: tagId, ...tagData });

        if (response) {
          // Update in Redux store
          dispatch(updateTag(response));

          // Track the event
          trackingEvent(
            'tag_updated',
            {
              ...getBasicAmplitudEventProperties(),
              tag_name: response.name,
              tag_id: response.id
            },
            AMPLITUDE_SERVICE
          );

          return response;
        }

        return null;
      } catch (err) {
        const errorMessage = err.message || 'Failed to update tag';
        setLocalError(errorMessage);
        return null;
      } finally {
        setLocalLoading(false);
      }
    },
    [dispatch]
  );

  /**
   * Deletes a tag
   */
  const deleteTag = useCallback(
    async (tagId) => {
      setLocalLoading(true);
      setLocalError(null);

      try {
        const response = await tagService.delete(tagId);

        if (response) {
          // Remove from Redux store
          dispatch(removeTag(tagId));

          // Track the event
          trackingEvent(
            'tag_deleted',
            {
              ...getBasicAmplitudEventProperties(),
              tag_id: tagId
            },
            AMPLITUDE_SERVICE
          );

          return true;
        }

        return false;
      } catch (err) {
        const errorMessage = err.message || 'Failed to delete tag';
        setLocalError(errorMessage);
        return false;
      } finally {
        setLocalLoading(false);
      }
    },
    [dispatch]
  );

  /**
   * Clears all tags from Redux store
   */
  const clearAllTags = useCallback(() => {
    dispatch(clearTags());
  }, [dispatch]);

  /**
   * Gets a tag by ID from Redux store
   */
  const getTagById = useCallback(
    (tagId) => tagsFromRedux.find((tag) => tag.id === tagId),
    [tagsFromRedux]
  );

  /**
   * Refreshes tags for current project
   */
  const refreshTags = useCallback(
    () => fetchTagsByProject(),
    [fetchTagsByProject]
  );

  return {
    // Data
    tags: tagsFromRedux,
    loading: loadingFromRedux || localLoading,
    error: errorFromRedux || localError,

    // Actions
    fetchTags,
    fetchTagsByProject,
    createTag,
    updateTag: updateTagById,
    deleteTag,
    clearAllTags,
    refreshTags,

    // Utilities
    getTagById
  };
};
